import { __assign, __awaiter, __generator } from "tslib";
import { TemplateCreateType } from "./types";
import { AccountAccessLevel, ProjectPlatform } from "./constants";
import {
  DuplicateNameError,
  InvalidNameError,
  NameTooLongError,
  ProjectCreationError,
  ProjectUserCreationError,
  UserNotDefinedError,
} from "./errors";
import { createProject } from "../CreateTemplateModal/requests/createProject";
import { createProjectUser } from "../CreateTemplateModal/requests/createProjectUser";
import { waitUntilUserAdded } from "../utils/waitUntilUserAdded";
// Functions taken from here:
// https://git.autodesk.com/BIM360/acc-project-list/blob/main/src/utils/accountHelpers.ts#L6-L38
//
// A project admin creation permission can either be:
// - an acsProjectAdmin (which is a user that is a project admin on at least 1 acs project in that account)
// - a project admin (a user on at least 1 project in the account) and the account is accEligible
// Additionally, if createProjectAccessLevels is present for the account, projectAdmin must be present on that array
export var hasProjectAdminCreationPermissions = function (account) {
  // NOTE: account.accessLevels is null if the user is not a member of any projects in the account
  return account.accessLevels
    ? (account.accessLevels.acsProjectAdmin ||
        (account.accessLevels.projectAdmin && account.isAccEligible)) &&
        (!account.createProjectAccessLevels ||
          account.createProjectAccessLevels.includes(
            AccountAccessLevel.ProjectAdmin
          ))
    : false;
};
// An account admin creation permission can either be an account admin on an account that:
// - hasAcsUnifiedProjects (i.e. at least 1 acs project)
// - isAccEligible (i.e. acc has been enabled for the account)
// Additionally, if createProjectAccessLevels is present for the account, accountAdmin must be present on that array
export var hasAccountAdminCreationPermissions = function (account) {
  return account.accessLevels
    ? account.accessLevels.accountAdmin &&
        (account.hasAcsUnifiedProjects || account.isAccEligible) &&
        (!account.createProjectAccessLevels ||
          account.createProjectAccessLevels.includes(
            AccountAccessLevel.AccountAdmin
          ))
    : false;
};
export var createOnSubmit = function (user, props) {
  return function (createTemplateRequest) {
    return __awaiter(void 0, void 0, void 0, function () {
      var userEmail,
        createProjectResponse,
        platform,
        e_1,
        error,
        isDuplicateName,
        isNameTooLong,
        createProjectUserResponse,
        error_1;
      return __generator(this, function (_a) {
        switch (_a.label) {
          case 0:
            // If the user doesn't exist - exit early
            if (!user) {
              props.onFailure &&
                props.onFailure(
                  new UserNotDefinedError(
                    "No data was returned from useOptionalUser()"
                  )
                );
              return [2 /*return*/];
            }
            if (!createTemplateRequest.name.trim()) {
              throw new InvalidNameError();
            }
            userEmail = user.email;
            platform = ProjectPlatform.Acc;
            _a.label = 1;
          case 1:
            _a.trys.push([1, 3, , 4]);
            return [
              4 /*yield*/,
              createProject(
                __assign(
                  {
                    name: createTemplateRequest.name,
                    accountId: createTemplateRequest.accountId,
                    platform: platform,
                  },
                  createTemplateRequest.type === TemplateCreateType.FromProject
                    ? {
                        type: TemplateCreateType.FromProject,
                        projectId: createTemplateRequest.projectId,
                      }
                    : {
                        type: TemplateCreateType.Blank,
                      }
                )
              ),
            ];
          case 2:
            createProjectResponse = _a.sent();
            return [3 /*break*/, 4];
          case 3:
            e_1 = _a.sent();
            error = e_1;
            isDuplicateName =
              error.errors &&
              error.errors.some(function (e) {
                return (
                  e.field === "name" &&
                  /^name:.+\sunique validation failed/i.test(e.detail)
                );
              });
            isNameTooLong =
              error.errors &&
              error.errors.some(function (e) {
                return (
                  e.field === "/name" &&
                  /^String is too long.+\smaximum \d{1,}/i.test(e.detail)
                );
              });
            if (isDuplicateName) {
              throw new DuplicateNameError();
            } else if (isNameTooLong) {
              throw new NameTooLongError();
            }
            // Call onFailure and expose error to be handled externally only if the error is
            // a non-user-handled error
            props.onFailure &&
              props.onFailure(new ProjectCreationError(JSON.stringify(error)));
            return [2 /*return*/];
          case 4:
            _a.trys.push([4, 6, , 7]);
            return [
              4 /*yield*/,
              createProjectUser({
                userEmail: userEmail,
                projectId: createProjectResponse.id,
                products: createProjectResponse.products,
              }),
            ];
          case 5:
            // Add the initial project admin
            createProjectUserResponse = _a.sent();
            return [3 /*break*/, 7];
          case 6:
            error_1 = _a.sent();
            props.onFailure &&
              props.onFailure(
                new ProjectUserCreationError(JSON.stringify(error_1))
              );
            return [2 /*return*/];
          case 7:
            // Wait until the user is added to the template
            return [
              4 /*yield*/,
              waitUntilUserAdded({
                projectId: createProjectResponse.id,
                userId: createProjectUserResponse.id,
              }),
            ];
          case 8:
            // Wait until the user is added to the template
            _a.sent();
            // Call onSuccess(), if it was provided
            props.onSuccess &&
              props.onSuccess({
                id: createProjectResponse.id,
                name: createProjectResponse.name,
                createdAt: createProjectResponse.createdAt,
                accountId: createTemplateRequest.accountId,
                platform: platform,
              });
            return [2 /*return*/];
        }
      });
    });
  };
};
